import {HttpClient} from '@angular/common/http';
import {EventEmitter, Injectable} from '@angular/core';
import {CoreCommands, CoreEvents, CoreStates} from '@app/models/commands/core-commands';
import {CurrentClass} from '@app/models/current-class';
import {VcrEntity} from '@app/models/vcr-entity/entity';
import {User} from '@app/models/vcr-entity/user';
import {LoginService} from '@app/modules/auth/services/login.service';
import {AccessType, ConnectionStatus, LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {SharedHandlersService} from '@app/modules/shared/services/shared-handlers.service';
import {AssignmentService} from '@app/modules/whiteboard/services/assignment.service';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {
  ClassroomMessage,
  DataService,
  MessageRecord,
  MsgRecordType,
  RecordArgs,
  RecordStack
} from '@app/services/data.service';
import {LogService} from '@app/services/log.service';
import {MessageHandler} from '@app/services/message-handler';
import * as RoomState from '@app/state/classroom-state.service';
import {ClassroomAction, ClassroomStateModel} from '@app/state/classroom-state.service';
import {GameModeAction, GameModeModel, GameModeState, Team} from '@app/state/game-mode.state';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {
  RemoteUserAction,
  RemoteUserAdditionalInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {Store} from '@ngxs/store';
import {StateReset} from 'ngxs-reset-plugin';
import {Observable, ObservableInput} from 'rxjs';
import {UsersListService} from '@app/modules/shared/services/users-list.service';
import {ModuleService} from '@app/services/module-service';
import {catchError, map} from 'rxjs/operators';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {BlackboardService} from '@app/modules/blackboard/services/blackboard.service';
import {ChatService} from '@app/modules/chat/services/chat.service';
import {WebPortalMessagingService} from '@app/services/web-portal-messaging.service';
import {Device} from '@app/models/device';
import {PrivateWhiteboardService} from '@app/modules/whiteboard/services/private-whiteboard.service';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {PrivateWhiteboard, PrivateWhiteboardState} from '@app/state/private-whiteboard.state';
import {UtilService} from '@app/modules/shared/services/util.service';
import {RoomConnection} from '@app/modules/wrtc/models/room-connection';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';
import {AssignmentState, AssignmentStateModel} from "@app/state/assignment.state";


export interface Timer {
  isOn: boolean;
  interval: any;
  durations: number[];
  selectedDuration: number;
  seconds: number;
}
export enum ApplauseLevel {
  INDIVIDUAL = 'INDIVIDUAL',
  CLASS = 'CLASS',
  TEAM = 'TEAM',
}

@Injectable({
  providedIn: 'root'
})
export class CoreService implements MessageHandler, ModuleService {
  private dataService: DataService;
  private classroom: ClassroomStateModel;
  private whiteboard: Whiteboard;
  private assignment: AssignmentStateModel;
  private privateWhiteboard: PrivateWhiteboard;
  private remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel } = {};
  private localUser: LocalUser;
  private currentSelectedClassParticipantIds = [];
  private gameModeModel: GameModeModel;
  public savingWhiteboardSnapshot: boolean = false;

  public applauseAudio: {
    individual: {id?: number; isPlaying: boolean; element: HTMLAudioElement; onEndCallback?: () => void};
    class: {id?: number; isPlaying: boolean; element: HTMLAudioElement; onEndCallback?: () => void};
    team: {id?: number; isPlaying: boolean; element: HTMLAudioElement; onEndCallback?: () => void};
  } = {
    individual: {isPlaying: false, element: new Audio()},
    class: {isPlaying: false, element: new Audio()},
    team: {isPlaying: false, element: new Audio()}
  };
  private static audioTimer: HTMLAudioElement = new Audio();
  private webPortalMessagingService: WebPortalMessagingService;
  public afterResetEmitter$: EventEmitter<void> = new EventEmitter<void>();

  public handleRecordStack: (recordStack: RecordStack) => Promise<void>; /* should be set in CoreCommands */

  public timer: Timer = {
    isOn: false,
    interval: undefined,
    durations: [], /* in minutes */
    selectedDuration: 35, /* in minutes */
    seconds: 0
  };

  constructor(
    private sharedHandlerService: SharedHandlersService,
    private assignmentService: AssignmentService,
    private wrtcService: WrtcService,
    private webSocketService: WebSocketService,
    private whiteboardService: WhiteboardService,
    private privateWhiteboardService: PrivateWhiteboardService,
    private blackboardService: BlackboardService,
    private chatService: ChatService,
    private loginService: LoginService,
    private usersListService: UsersListService,
    private utilService: UtilService,
    private http: HttpClient,
    private store: Store,
  ) {
    this.listenStoreChanges();

    this.applauseAudio.individual.onEndCallback = () => {
      this.applauseAudio.individual.isPlaying = false;
      this.applauseAudio.individual.id = undefined;
      this.applauseAudio.individual.element.removeEventListener('ended', this.applauseAudio.individual.onEndCallback);
    };
    this.applauseAudio.class.onEndCallback = () => {
      this.applauseAudio.class.isPlaying = false;
      this.applauseAudio.class.element.removeEventListener('ended', this.applauseAudio.class.onEndCallback);
    };
    this.applauseAudio.team.onEndCallback = () => {
      this.applauseAudio.team.isPlaying = false;
      this.applauseAudio.team.id = undefined;
      this.applauseAudio.team.element.removeEventListener('ended', this.applauseAudio.team.onEndCallback);
    };

    this.applauseAudio.individual.element.src = 'assets/audio/claps.mp3';
    this.applauseAudio.class.element.src = 'assets/audio/claps.mp3';
    this.applauseAudio.team.element.src = 'assets/audio/claps.mp3';
  }

  public setDataService(dataService: DataService): void {
    this.dataService = dataService;
  }

  public init(): void {
    this.getState().subscribe();
  }

  public getState(): Observable<any> {
    this.store.dispatch(new ClassroomAction.SetCurrentClassInfo(null));
    Object.keys(this.remoteUsers).forEach((idStr: string) => {
      const id: number = parseInt(idStr);
      this.store.dispatch(new RemoteUserAction.RiseHand(id, false));
      this.store.dispatch(new RemoteUserAction.SetStickers(id, []));
    });
    this.store.dispatch(new LocalUserAction.SetLocalUserRaiseHand(false));
    this.stopTimer();

    effects.turnOffFirework(false);

    this.store.dispatch(new LocalUserAction.SetStickers([]));

    this.store.dispatch(new GameModeAction.StartGameMode());
    this.store.dispatch(new StateReset(GameModeState));
    this.wrtcService.updateParticipantsSubscriptionRules();

    return this.http.get(`${this.classroom.vcrURL}/classroom/get-state`)
      .pipe(map((result: any) => {
        console.log(`GET STATE - ${result.message}`);
        this.handleRecordStack(result.data as RecordStack).then();
      }));
  }

  public initClassmates(classmates: User[]): void {
    classmates?.forEach((user: User) => {
      if (user.id !== this.localUser.id) {
        this.addRemoteUser(VcrEntity.createEntity(User, user));
      }
    });
    this.usersListService.sortUsers();
  }

  public setCurrentClass(currentClass: CurrentClass): void {
    if (!currentClass) {
      return;
    }
    if (this.classroom.currentClass?.groupId !== currentClass.groupId) {
      this.store.dispatch(new ClassroomAction.SetCurrentClassInfo(currentClass));
      this.getStudentsByGroupId(currentClass.groupId, currentClass.additionalInfo.lessonId).subscribe((result: any) => {
        const classUsers: User[] = result.data.map(user => VcrEntity.createEntity(User, user));
        this.updateClassParticipants(classUsers);
      });
    } else {
      LogService.writeLog(`Try reselect select group ${currentClass.groupId}`, LogLevel.WARN, LogResource.CORE);
    }
  }

  public sendLogout(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        CoreEvents.LOGOUT,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/b`, message);
  }
  public onLogout(record: MessageRecord) {
    this.userLogout(record.ownerId);
  }

  public sendChannelOpen(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        CoreEvents.CHANNEL_OPEN,
        new RecordArgs(JSON.stringify(this.localUser))
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/b`, message);
  }
  public onChannelOpen(record: MessageRecord): void {
    const user: User = JSON.parse(record.data);

    this.addRemoteUser(VcrEntity.createEntity(User, user));
  }

  public sendChannelReload(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        CoreEvents.CHANNEL_RELOAD,
        new RecordArgs(JSON.stringify(this.localUser))
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/b`, message);
  }
  public onChannelReload(record: MessageRecord): void {
    const recordData: any = JSON.parse(record.data);

    if (this.localUser.accessType !== AccessType.STUDENT) {
    }
  }

  public sendEndLesson(): Observable<any> {
    return this.http.get(`${this.classroom.vcrURL}/classroom/purge`);
  }
  public onEndLesson(): void {
    this.loginService.logout(this.localUser.isGuest, false).subscribe((redirectURL: any): void => {
      window.location = redirectURL;
    });
  }

  public sendSetClass(currentClass: CurrentClass): Observable<any> {
    const recordData: string = JSON.stringify({currentClass});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.CURRENT_CLASS,
        new RecordArgs(recordData)
      )
    };

    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onSetClass(record: MessageRecord): void {
    const currentClass: CurrentClass = JSON.parse(record.data).currentClass;
    this.setCurrentClass(currentClass);
  }

  public sendToggleDND(isOn: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.DO_NOT_DISTURB,
        new RecordArgs(isOn + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onToggleDND(record: MessageRecord): void {
    const isOn: boolean = record.data === 'true';
    this.toggleDND(isOn);
  }

  public sendRaiseHand(): Observable<any> {
    const recordData: string = JSON.stringify([this.localUser.id]);
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        CoreStates.RAISE_HAND,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/add`, message);
  }
  public onRaiseHand(record: MessageRecord): void {
    const userIds: number[] = JSON.parse(record.data);
    userIds.forEach((id: number) => {
      if (id === this.localUser.id) {
        this.store.dispatch(new LocalUserAction.SetLocalUserRaiseHand(true));
      } else {
        this.store.dispatch(new RemoteUserAction.RiseHand(id, true));
      }
    });
  }

  /* if targetId is not set, cansel self rise hand */
  public sendCancelRaiseHand(targetId?: number): Observable<any> {
    const recordData: string = JSON.stringify([targetId ? targetId : this.localUser.id]);
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      mirrorState: CoreStates.RAISE_HAND,
      record: this.buildRecord(
        CoreStates.CANCEL_RAISE_HAND,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/sub`, message);
  }
  public onCancelRaiseHand(record: MessageRecord): void {
    const userIds: number[] = JSON.parse(record.data);
    userIds.forEach((id: number) => {
      if (id === this.localUser.id) {
        this.store.dispatch(new LocalUserAction.SetLocalUserRaiseHand(false));
      } else {
        this.store.dispatch(new RemoteUserAction.RiseHand(id, false));
      }
    });
  }

  public sendBreakTime(duration: number): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.BREAK_TIMER,
        new RecordArgs(duration + '')
      )
    };
    return this.http.post(`${this.classroom.vcrURL}/classroom/timer`, message, {params: {turnOn: true}});
  }
  public onBreakTimer(record: MessageRecord): void {
    const endTimestamp: number = parseInt(record.data); /* server changes minutes to end duration */
    this.startTimer(endTimestamp);
  }

  public sendStopBreakTime(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      mirrorState: CoreStates.BREAK_TIMER,
      record: this.buildRecord(
        CoreStates.STOP_BREAK_TIMER,
        new RecordArgs('')
      )
    };
    return this.http.post(`${this.classroom.vcrURL}/classroom/timer`, message, {params: {turnOn: false}});
  }
  public onStopBreakTimer(): void {
    this.stopTimer();
  }

  public sendFullScreen(): Observable<any> {
    const recordData: string = JSON.stringify([this.localUser.id]);
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        CoreStates.FULLSCREEN,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/add`, message);
  }
  public onFullScreen(record: MessageRecord): void {
    const userIds: number[] = JSON.parse(record.data);

    userIds.forEach((id: number) => {
      this.store.dispatch(new RemoteUserAction.SetIsInFullscreenMode(id, true));
    });
  }

  public sendFullScreenOut(): Observable<any> {
    const recordData: string = JSON.stringify([this.localUser.id]);
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      mirrorState: CoreStates.FULLSCREEN,
      record: this.buildRecord(
        CoreStates.FULLSCREEN_OUT,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/sub`, message);
  }
  public onFullScreenOut(record: MessageRecord): void {
    const userIds: number[] = JSON.parse(record.data);
    userIds.forEach((id: number) => {
      this.store.dispatch(new RemoteUserAction.SetIsInFullscreenMode(id, false));
    });
  }

  public sendAddSticker(sticker: string, targetId: number): Observable<any> {
    const stickers = {};
    stickers[targetId] = this.remoteUsers[targetId].stickers.concat(sticker);
    const recordData: string = JSON.stringify(stickers);

    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.STICKERS,
        new RecordArgs(recordData, targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/set-stickers?add=true&sticker=${sticker}`, message);
  }

  public sendAddTeamSticker( teamId: number): Observable<any> {
    const stickers: { [key: number]: string[] } = {};
    for (const team of this.gameModeModel.teams){
      if (team.id === teamId) {
        for (const student of team.participantIds) {
          stickers[student] = this.remoteUsers[student].stickers.concat('winner');
        }
      }
    }

    const recordData: string = JSON.stringify(stickers);

    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.STICKERS,
        new RecordArgs(recordData,)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/set-stickers?add=true&sticker=team`, message);
  }

  public sendRemoveTeamSticker( teamId: number): Observable<any> {
    const stickers: { [key: number]: string[] } = {};
    for (const team of this.gameModeModel.teams){
      if (team.id === teamId){
        for (const student of team.participantIds){
          for (let j = this.remoteUsers[student].stickers.length - 1; j >= 0; j--) {
            if (this.remoteUsers[student].stickers[j] === 'winner') {
              this.remoteUsers[student].stickers.splice(j, 1);
              stickers[student] = this.remoteUsers[student].stickers;
              break;
            }
          }
        }
      }
    }
    const recordData: string = JSON.stringify(stickers);

    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.STICKERS,
        new RecordArgs(recordData,)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/set-stickers?add=false`, message);
  }

  public sendRemoveSticker(targetId: number): Observable<any> {
    const stickers = {};
    stickers[targetId] = this.remoteUsers[targetId].stickers.slice(0, -1);
    const data: string = JSON.stringify(stickers);

    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.STICKERS,
        new RecordArgs(data, targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/set-stickers?add=false`, message);
  }
  public onSticker(record: MessageRecord): void {
    const stickers: { [key: number]: string[] } = JSON.parse(record.data);
    Object.keys(stickers).forEach((idStr: string) => {
      const id = parseInt(idStr);
      if (this.localUser.id === id) {
        this.store.dispatch(new LocalUserAction.SetStickers(stickers[id]));
      } else {
        this.store.dispatch(new RemoteUserAction.SetStickers(id, stickers[id]));
      }
    });
  }

  public sendTeacherNickname(nickname: string): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.TEACHER_NICKNAME,
        new RecordArgs(nickname)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/teacher-nickname`, message);
  }

  public onTeacherNickname(record: MessageRecord): void {
    if (this.localUser.id === record.ownerId) {
      this.store.dispatch(new LocalUserAction.SetNickname(record.data));
    } else {
      this.store.dispatch(new RemoteUserAction.SetNickname(record.ownerId, record.data));
    }
  }

  public sendRedirectStudent(classroomId: number, targetId: number): Observable<any> {
    const data: string = classroomId + '';
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        CoreEvents.REDIRECT_STUDENT,
        new RecordArgs(data, targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/tp`, message);
  }
  public onRedirectStudent(record: MessageRecord): void {
    const classroomNum: string = record.data;
    if (this.localUser.id === record.targetId) {
      this.sendLogout().subscribe(() => {
        this.loginService.logout().subscribe(() => {
          this.chatService.sendSystemInfoMessage(this.localUser,' logged out.');
          window.location.href = this.constructRedirectToClassroomUrl(classroomNum, this.localUser.isGuest, this.localUser.name);
        });
      });
    } else {
      this.userLogout(record.targetId);
    }
  }

  public sendRedirectClassroom(classroomNumber: number): Observable<any> {
    const data: string = JSON.stringify({classroomNumber});
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreEvents.REDIRECT_CLASSROOM,
        new RecordArgs(data)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onRedirectClassroom(record: MessageRecord): void {
    const recordData: any = JSON.parse(record.data);
    this.sendLogout().subscribe(() => {
      this.loginService.logout().subscribe(() => {
        this.chatService.sendSystemInfoMessage(this.localUser,' logged out.');
        window.location.href = this.constructRedirectToClassroomUrl(recordData.classroomNumber, this.localUser.isGuest, this.localUser.name);
      });
    });
  }

  public sendExpelStudent(targetId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreEvents.EXPEL_STUDENT,
        new RecordArgs('', targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onExpelStudent(record: MessageRecord): void {
    if (record.targetId === this.localUser.id) {
      this.loginService.logout(this.localUser.isGuest).subscribe(redirectUrl => window.location = redirectUrl);
    } else {
      this.userLogout(record.targetId);
    }
  }

  public sendExpelStaff(targetId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreEvents.EXPEL_STAFF,
        new RecordArgs('', targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onExpelStaff(record: MessageRecord): void {
    if (record.targetId === this.localUser.id) {
      this.loginService.logout().subscribe(redirectUrl => window.location = redirectUrl);
    } else {
      this.userLogout(record.targetId);
    }
  }

  public sendTeamsInfo(teams: Team[], lastTeamId: number): Observable<any> {
    const recordValue: string = JSON.stringify({teams, lastTeamId});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.TEAMS_INFO,
        new RecordArgs(recordValue)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onTeamInfo(record: MessageRecord) {
    const recordValue: any = JSON.parse(record.data);
    this.store.dispatch(new GameModeAction.UpdateTeamsInfo(recordValue.teams, recordValue.lastTeamId));
    this.wrtcService.updateParticipantsSubscriptionRules();
    this.usersListService.sortUsers();
  }

  public sendTeamName(teamId: number, teamName: string): Observable<any> {
    const recordValue: string = JSON.stringify({teamId, teamName});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        CoreStates.TEAM_NAME,
        new RecordArgs(recordValue)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onTeamName(record: MessageRecord) {
    const recordValue: any = JSON.parse(record.data);

    this.store.dispatch(new GameModeAction.UpdateTeamName(recordValue.teamId, recordValue.teamName));
  }

  public sendTeamTalk(isOn: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.TEAM_TALK,
        new RecordArgs(isOn + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onTeamTalk(record: MessageRecord) {
    const isOn: boolean = record.data === 'true';
    if (isOn) {
      this.store.dispatch(new GameModeAction.ToggleTeamTalk(true));
    } else {
      this.store.dispatch(new GameModeAction.ToggleTeamTalk(false));
    }
    this.wrtcService.updateParticipantsSubscriptionRules();
  }

  public sendGameMode(isOn: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.GAME_MODE,
        new RecordArgs(isOn + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onGameMode(record: MessageRecord) {
    const isOn: boolean = record.data === 'true';
    if (isOn) {
      this.store.dispatch(new GameModeAction.StartGameMode());
    } else {
      this.store.dispatch(new StateReset(GameModeState));
      this.wrtcService.updateParticipantsSubscriptionRules();
    }
  }

  public sendTeamScore(teamId: number, score: number): Observable<any> {
    const recordData: {[key: number]: number} = {};
    recordData[teamId] = score;

    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreStates.TEAM_SCORE,
        new RecordArgs(JSON.stringify(recordData))
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/set-team-score`, message);
  }
  public onTeamScore(record: MessageRecord): void {
    const recordData: { [key: number]: number } = JSON.parse(record.data);
    Object.keys(recordData).forEach((teamId: string): void => {
      this.store.dispatch(new GameModeAction.SetTeamScore(Number(teamId), recordData[teamId]));
    });
  }

  public sendResetTeamScore(): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      mirrorState: CoreStates.TEAM_SCORE,
      record: this.buildRecord(
        CoreEvents.RESET_TEAM_SCORE,
        new RecordArgs(JSON.stringify({}))
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/reset-teams-scores`, message);
  }
  public onResetTeamScore(record: MessageRecord): void {
    const recordData: { [key: number]: number } = JSON.parse(record.data);
    Object.keys(recordData).forEach((teamId: string): void => {
      this.store.dispatch(new GameModeAction.SetTeamScore(Number(teamId), recordData[teamId]));
    });
  }

  public sendFireworkOn(): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        CoreStates.FIREWORK_ON,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onFireworkOn(record: MessageRecord): void {
    effects.turnOnFirework(false);
  }

  public sendFireworkOff(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      mirrorState: CoreStates.FIREWORK_ON,
      record: this.buildRecord(
        CoreStates.FIREWORK_OFF,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/del`, message);
  }
  public onFireworkOff(record: MessageRecord): void {
    effects.turnOffFirework(false);
  }

  public sendResetLesson(): Observable<any> {
    return this.http.get(`${this.classroom.vcrURL}/classroom/reset-lesson`);
  }

  public sendResetUsers(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        CoreEvents.RESET_USERS,
        new RecordArgs('')
      )
    };
    return this.http.post(`${this.classroom.vcrURL}/classroom/reset-users`, message);
  }
  public onResetUsers(record: MessageRecord): void {
    if (this.localUser.id !== record.ownerId && this.localUser.accessType !== AccessType.STUDENT) {
      this.loginService.selectAccessType(this.localUser.accessType).subscribe();
    }
    this.resetClassroom().then();
    this.afterResetEmitter$.emit();
  }

  public onAddProblem(record: MessageRecord): void {
    if (this.classroom.privateTalkInfo.isPrivateWhiteboardOn) {
      this.privateWhiteboardService.onAddProblem(record);
    } else {
      this.whiteboardService.onAddProblem(record);
    }
  }

  //Sends applause to a particular student
  public sendIndividualApplause(id: number): Observable<any> {
    const recordData: string = JSON.stringify({
      applauseLevel: ApplauseLevel.INDIVIDUAL,
      id
    });
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        CoreEvents.APPLAUSE,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }

  //Sends applause to a team
  public sendTeamApplause(id: number): Observable<any> {
    const recordData: string = JSON.stringify({
      applauseLevel: ApplauseLevel.TEAM,
      id
    });
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        CoreEvents.APPLAUSE,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  //Sends applause to the entire class
  public sendApplauseToClass(): Observable<any> {
    const recordData: string = JSON.stringify({
      applauseLevel: ApplauseLevel.CLASS
    });
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        CoreEvents.APPLAUSE,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onApplause(record: MessageRecord): void {
    const recordData: {applauseLevel: ApplauseLevel; id: number} = JSON.parse(record.data);
    switch (recordData.applauseLevel) {
      case ApplauseLevel.INDIVIDUAL:
        if (this.localUser.id !== recordData.id && this.localUser.accessType === AccessType.STUDENT) {
          return;
        }
        break;
      case ApplauseLevel.TEAM:
        const team: Team = this.gameModeModel.teams.find((t: Team) => t.id === recordData.id);
        if (!team.participantIds.includes(this.localUser.id) && this.localUser.accessType === AccessType.STUDENT) {
          return;
        }
        break;
    }

    this.applause(recordData.applauseLevel, recordData.id);
  }

  public sendWhiteboardSnapshotSavingStatus(status: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        CoreStates.WB_SNAPSHOT_SAVING_STATUS,
        new RecordArgs(status + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/t`, message);
  }
  public onWhiteboardSnapshotSavingStatus(record: MessageRecord): void {
    this.savingWhiteboardSnapshot = record.data === 'true';
  }

  public sendStartSpeaking(userId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: false,
      selfProcess: false,
      record: this.buildRecord(
        CoreEvents.START_SPEAKING,
        new RecordArgs(userId + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/b`, message);
  }
  public onStartSpeaking(record: MessageRecord): void {}

  public sendEndSpeaking(userId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: false,
      selfProcess: false,
      record: this.buildRecord(
        CoreEvents.END_SPEAKING,
        new RecordArgs(userId + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/classroom/b`, message);
  }
  public onEndSpeaking(record: MessageRecord): void {}

  public exportAndSaveWhiteboardSnapshot(): Promise<void> {
    const isWhiteboardEmpty: boolean =
      this.whiteboard.container.__gridGroup__.children.length === 0 &&
      this.whiteboard.container.__elementsGroup__.children.length === 0 &&
      this.whiteboard.container.__highlightsGroup__.children.length === 0 &&
      this.whiteboard.container.__pointersGroup__.children.length === 0 &&
      this.whiteboard.container.__nodesGroup__.children.length === 0;

    if (!this.classroom.currentClass || isWhiteboardEmpty) {
      return Promise.resolve();
    }

    const groupId: number = this.classroom.currentClass.groupId;
    const lessonNumber: number = this.utilService.parseLessonNumber(this.classroom.currentClass.additionalInfo.lesson);

    if (groupId && lessonNumber) {
      this.whiteboard.container.blur();
      this.sendWhiteboardSnapshotSavingStatus(true).subscribe();

      this.savingWhiteboardSnapshot = true;
      return this.whiteboardService.exportAndSaveSnapshot(groupId, lessonNumber)
        .then()
        .catch()
        .finally(() => {
          this.savingWhiteboardSnapshot = false;
          this.sendWhiteboardSnapshotSavingStatus(false).subscribe();
        });
    }

    return Promise.reject();
  }

  //An actual applause sound is being played, this method you can call whenever and wherever you want to use applause sound
  public applause(applauseLevel: ApplauseLevel, id?: number): void {
    switch (applauseLevel) {
      case ApplauseLevel.INDIVIDUAL:
        this.applauseAudio.individual.isPlaying = true;
        this.applauseAudio.individual.id = id;
        this.applauseAudio.individual.element.load();
        this.applauseAudio.individual.element.play().then(() => {
          this.applauseAudio.individual.element.addEventListener('ended', this.applauseAudio.individual.onEndCallback);
        });
        break;
      case ApplauseLevel.CLASS:
        this.applauseAudio.class.isPlaying = true;
        this.applauseAudio.class.element.load();
        this.applauseAudio.class.element.play().then(() => {
          this.applauseAudio.class.element.addEventListener('ended', this.applauseAudio.class.onEndCallback);
        });
        break;
      case ApplauseLevel.TEAM:
        this.applauseAudio.team.isPlaying = true;
        this.applauseAudio.team.id = id;
        this.applauseAudio.team.element.load();
        this.applauseAudio.team.element.play().then(() => {
          this.applauseAudio.team.element.addEventListener('ended', this.applauseAudio.team.onEndCallback);
        });
        break;
    }
  }

  private initAudioElement(audioElement: HTMLAudioElement): void {
    audioElement.muted = true;
    audioElement.play().then(()=> {
      audioElement.pause();
      audioElement.muted = false;
    });
  }

  public shareWhiteboards(): void {
    this.whiteboardService.sendShare();
    this.privateWhiteboardService.sendShare();
  }
  public shareWhiteboardsWith(userId: number): void {
    this.whiteboardService.sendShareWith(userId);
    this.privateWhiteboardService.sendShareWith(userId);
  }

  public unshareWhiteboards(): void {
    this.whiteboardService.sendUnshare();
    this.privateWhiteboardService.sendUnshare();
  }
  public unshareWhiteboardsWith(userId: number): void {
    this.whiteboardService.sendUnshareWith(userId);
    this.privateWhiteboardService.sendUnshareWith(userId);
  }

  public toggleWhiteboardsSharingWith(userId: number): void {
    if (this.whiteboard.sharedUsers?.has(userId)) {
      this.whiteboardService.sendUnshareWith(userId);
    } else {
      this.whiteboardService.sendShareWith(userId);
    }

    if (this.privateWhiteboard.sharedUsers?.has(userId)) {
      this.privateWhiteboardService.sendUnshareWith(userId);
    } else {
      this.privateWhiteboardService.sendShareWith(userId);
    }
  }

  public toggleAssignmentSwitchAccessWith(userId: number): void {
    if (this.assignment.allowedSwitchingUsers?.size === 0) {
      return;
    }

    if (this.assignment.allowedSwitchingUsers?.has(userId)) {
      this.assignmentService.sendDisallowSwitchFor(userId).subscribe();
    } else {
      this.assignmentService.sendAllowSwitchFor(userId).subscribe();
    }
  }

  /**
   * for Ipad...
   * */
  public initApplauseAudio(): void {
    this.initAudioElement(this.applauseAudio.individual.element);
    this.initAudioElement(this.applauseAudio.class.element);
    this.initAudioElement(this.applauseAudio.team.element);
  }

  public getOfflineStudents(): RemoteUserAdditionalInfoStateModel[] {
    return Object.keys(this.remoteUsers)
      .filter(userId => this.remoteUsers[userId].roomConnectionStatus !== ConnectionStatus.ONLINE &&
        this.remoteUsers[userId].isClassParticipant &&
        !this.remoteUsers[userId].isUserConnectedDuringSession &&
        this.remoteUsers[userId].accessType === AccessType.STUDENT)
      .map(value => this.remoteUsers[value]);
  }

  public getClassStudentIds(): number[] {
    return Object.keys(this.remoteUsers)
      .filter(userId =>
        this.remoteUsers[userId].isClassParticipant &&
        this.remoteUsers[userId].accessType === AccessType.STUDENT)
      .map(id => parseInt(id));
  }

  public constructRedirectToClassroomUrl(classroomNumber: string, isGuest: boolean, name: string): string {
    return '/' + classroomNumber + (isGuest ? '/guest/' + name : '');
  }

  private toggleDND(isOn: boolean): void {
    this.store.dispatch(new ClassroomAction.ToggleDnDMode(isOn));
  }

  private updateClassParticipants(classParticipants: User[]): void {
    this.currentSelectedClassParticipantIds = classParticipants.map(value => value.id);
    this.removeClassParticipants();

    for (const user of classParticipants) {
      this.addRemoteUser(user, false, true, false);
    }
  }

  private removeClassParticipants(): void {
    const studentsToRemove: RemoteUserAdditionalInfoStateModel[] = this.getOfflineStudents();
    this.getClassStudentIds().forEach(userId => {
      this.store.dispatch(new RemoteUserAction.SetIsClassParticipant(userId, false));
    });
    studentsToRemove.forEach((remoteUser: RemoteUserAdditionalInfoStateModel) => {
      this.userLogout(remoteUser.id);
    });
  }

  public userLogout(userId: number): void {
    const loggedOutUser: RemoteUserAdditionalInfoStateModel = this.remoteUsers[userId];

    this.store.dispatch(new ClassroomAction.ClearUserMessages(userId, false));
    this.store.dispatch(new RemoteUserAction.LogoutUser(userId));
    // todo revisit user logout flow
    // if(!loggedOutUser.isClassParticipant){
    //   this.remoteUsersWrapper.removeUser(userId, loggedOutUser.accessType);
    // }
   // }
  }

  private startTimer(endTimestamp: number): void {
    if (this.classroom.isTimerEnabled) {
      this.pauseTimer();
    } else {
      this.store.dispatch(new ClassroomAction.ToggleTimer(true));
    }

    this.timer.seconds = Math.floor((endTimestamp - Date.now()) / 1000);
    if (this.timer.seconds <= 0) {
      this.timer.seconds = 0;
      return;
    }
    this.timer.interval = setInterval(() => {
      this.timer.seconds = Math.floor((endTimestamp - Date.now()) / 1000);
      if (this.timer.seconds <= 1) {
        this.pauseTimer();
        CoreService.playTimerNotificationSound();
      }
    }, 1000);
  }

  public static playTimerNotificationSound(firstEnter: boolean = false): void {
    this.audioTimer.src = 'assets/audio/timer-notification.mp3';
    if (firstEnter){
      this.audioTimer.muted=true;
      this.audioTimer.play().then(()=>{
        this.audioTimer.pause();
        this.audioTimer.muted=false;
      });
    }else {
      this.audioTimer.load();
      this.audioTimer.play().then();
    }
  }

  private pauseTimer(): void {
    clearInterval(this.timer.interval);
    this.timer.interval = undefined;
    this.timer.seconds = 0;
  }

  private stopTimer(): void {
    this.pauseTimer();
    this.store.dispatch(new ClassroomAction.ToggleTimer(false));
  }

  public getStudentsByGroupId(groupId: number, lessonId: number): Observable<any> {
    return this.http.get(`${this.classroom.vcrURL}/classroom/get-students-by-group-id?groupId=${groupId}&lessonId=${lessonId}`);
  }

  public buildRecord(command: string, args: RecordArgs): MessageRecord {
    const msgType = CoreStates[command] ? MsgRecordType.STATE : CoreEvents[command] ? MsgRecordType.EVENT : null;
    return {
      category: CoreCommands.category,
      name: command,
      data: args.data,
      type: msgType,
      ownerId: null, /* must be set server-side on receive */
      targetId: args.targetId
    };
  }

  private addRemoteUser(user: User, isConnectedDuringLesson: boolean = true, isClassParticipant: boolean = false, addMessage: boolean = true): void {
    if (!this.remoteUsers[user.id]) {
      const additionalInfo = new RemoteUserAdditionalInfoStateModel(user, isConnectedDuringLesson);
      this.usersListService.remoteUsersWrapper.addUser(user, additionalInfo);
      this.store.dispatch(new RemoteUserAction.AddUser(additionalInfo));
      this.store.dispatch(new RemoteUserAction.SetIsClassParticipant(user.id, isClassParticipant));
    } else {
      this.store.dispatch(new RemoteUserAction.SetIsClassParticipant(user.id, isClassParticipant || this.remoteUsers[user.id].isClassParticipant));

      if (
        this.remoteUsers[user.id].roomConnectionStatus !== ConnectionStatus.ONLINE ||
        user.accessType !== this.remoteUsers[user.id].accessType /* access type changed */
      ) {
        if (user.accessType !== this.remoteUsers[user.id].accessType) {
          this.usersListService.remoteUsersWrapper.changeAccessType(user.id, user.accessType);
          this.store.dispatch(new RemoteUserAction.SetUserAccessType(user.id, user.accessType));
          this.store.dispatch(new RemoteUserAction.SetUserIdentity(user.id, user.identity));
        }
        this.store.dispatch(new RemoteUserAction.SetIsConnectedDuringSession(user.id, isConnectedDuringLesson));
      }
    }

    this.usersListService.sortUsers();

    if (this.classroom.isClassroomMuted) {
      this.store.dispatch(new RemoteUserAction.SetUserAudioMute(user.id, true));
    }
  }

  private listenStoreChanges(): void {
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
    this.store.select(RemoteUsersInfoState.getUsersInfo).subscribe(usersInfoById => {
      this.remoteUsers = usersInfoById;
    });
    this.store.select(RoomState.ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard) => {
      this.whiteboard = whiteboard;
    });
    this.store.select(PrivateWhiteboardState).subscribe((whiteboard: PrivateWhiteboard) => {
      this.privateWhiteboard = whiteboard;
    });
    this.store.select(GameModeState).subscribe((gameModeModel: GameModeModel) => {
      this.gameModeModel = gameModeModel;
    });
    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });
  }

  private send(destination: string, data: any, options?: object): Observable<object> {
    return this.http.post(destination, data, options);
  }

  public sendRemindMessages(studentId: number, studentName): Observable<any> {
    return this.send(`${this.classroom.vcrURL}/classroom/students/${studentId}/send-email`, studentName);
  }

  public sendDevice(device: Device): Observable<any> {
    return this.send(`${this.classroom.vcrURL}/classroom/user-device-changed`, device);
  }

  public sendBitrate(bitrate: number): Observable<any> {
    return this.send(`${this.classroom.vcrURL}/classroom/user-bitrate-calculated`, bitrate);
  }

  public resetClassroom(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.wrtcService.reconnectToRoom()
        .pipe(catchError((error: any, caught: Observable<RoomConnection>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();

      this.wrtcService.getState()
        .pipe(catchError((error: any, caught: Observable<any>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();

      this.store.dispatch(new ClassroomAction.ClearAllMessages());
      this.store.dispatch(new RemoteUserAction.ClearAllMessages());
      this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(false));

      this.getState()
        .pipe(catchError((error: any, caught: Observable<any>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();
      this.whiteboardService.getState()
        .pipe(catchError((error: any, caught: Observable<any>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();
      this.blackboardService.getState()
        .pipe(catchError((error: any, caught: Observable<any>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();
      this.assignmentService.getState()
        .pipe(catchError((error: any, caught: Observable<any>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();
      this.chatService.getState()
        .pipe(catchError((error: any, caught: Observable<any>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();

      this.sendChannelOpen()
        .pipe(catchError((error: any, caught: Observable<any>): ObservableInput<any> => {
          reject(error);
          return caught;
        }))
        .subscribe();

      resolve();
    });
  }

  public setWebPortalService(webPortalMessagingService: WebPortalMessagingService): void {
    this.webPortalMessagingService = webPortalMessagingService;
  }
}
